import type { DeepLinkDetails, DeepLinkSubType, DeepLinkTargetType, DeepLinkType } from './types';
import { deepLinkSubTypes, deepLinkTargetTypes, deepLinkTypes } from './types';

const DeepLinkTypeKey = 'type';
const DeepLinkDataKey = 'data=';

type ParseDeepLink = (subEntityId?: string) => DeepLinkDetails;
export const parseDeepLink: ParseDeepLink = (subEntityId) => {
  // Format - type = <DeepLinkType>, data = <EntityType>:<EntityId>:<DeepLinkSubType>:<DeepLinkId>

  if (!subEntityId) {
    throw new Error('parseDeepLink - Invalid SubEntityId');
  }

  const typeDataParts = subEntityId.split(',');
  if (typeDataParts.length !== 2) {
    throw new Error(`parseDeepLink - Split length (${typeDataParts.length}) is not expected`);
  }

  const deepLinkType = getDeepLinkType(typeDataParts[0]);

  return getDeepLinkDetails(deepLinkType, typeDataParts[1]);
};

const getDeepLinkType = (typePart: string): DeepLinkType => {
  const typeKeyValue = typePart.split('=');

  if (typeKeyValue.length !== 2) {
    throw new Error(`getDeepLinkType - typeKeyValue split length (${typeKeyValue.length}) is not expected`);
  }

  const typeKey = typeKeyValue[0].toLowerCase().trim();
  const typeValue = typeKeyValue[1].toLowerCase().trim() as DeepLinkType;

  if (typeKey !== DeepLinkTypeKey) {
    throw new Error('getDeepLinkType - Invalid typeKey');
  }

  if (!deepLinkTypes.includes(typeValue)) {
    throw new Error('getDeepLinkType - invalid typeValue');
  }

  return typeValue;
};

const getDeepLinkDetails = (deepLinkType: DeepLinkType, dataPart: string): DeepLinkDetails => {
  const regEscape = (v: string) => v.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  const dataKeyValue = dataPart.split(new RegExp(regEscape(DeepLinkDataKey), 'ig'));

  if (dataKeyValue.length !== 2) {
    throw new Error(
      `getDeepLinkDetails(type: ${deepLinkType}) - dataKeyValue split length (${dataKeyValue.length}) is not expected`
    );
  }

  const dataValue = dataKeyValue[1].trim();

  const dataValueParts = dataValue.split(':');
  if (isInvalidLength(deepLinkType, dataValueParts)) {
    throw new Error(
      `getDeepLinkDetails(type: ${deepLinkType}) - dataValueParts split length (${dataValueParts.length}) is not expected`
    );
  }

  const entityType = dataValueParts[0].toLowerCase().trim() as DeepLinkTargetType;
  // Teams QnA app passes base64 encoded graph id so the "==" is added at the end
  // This is a workaround for this BE issue by removing all "="
  const isUserAchievementEntityType = entityType === 'userachievement';
  const entityId = isUserAchievementEntityType ? dataValueParts[1].trim() : dataValueParts[1].replace(/=/g, '').trim();
  let deepLinkSubType;
  let correlationId;
  let notificationType;
  if (isTeamsQnAEnabledForMeetingSubType(dataValueParts[0])) {
    deepLinkSubType = dataValueParts[0].trim() as DeepLinkSubType;
    correlationId = dataValueParts[1].trim();
  } else {
    deepLinkSubType = (dataValueParts[2] ? dataValueParts[2].trim() : undefined) as DeepLinkSubType;
    correlationId = dataValueParts[3] ? dataValueParts[3].trim() : undefined;
    notificationType = dataValueParts[4] ? dataValueParts[4].trim() : undefined;

    if (!deepLinkTargetTypes.includes(entityType)) {
      throw new Error(`getDeepLinkType(type: ${deepLinkType}) - Invalid entityType`);
    }
  }

  if (deepLinkSubType && !isValidDeepLinkSubType(deepLinkSubType)) {
    throw new Error(`getDeepLinkType(type: ${deepLinkType}) - Invalid deepLinkSubType`);
  }

  return {
    entityId,
    entityType,
    correlationId,
    subType: deepLinkSubType,
    type: deepLinkType,
    notificationType,
  };
};

const isValidDeepLinkSubType = (deepLinkSubType: string): boolean =>
  !!deepLinkSubTypes.find((subType) => subType.toLowerCase() === deepLinkSubType.toLowerCase());

const isInvalidLength = (deepLinkType: DeepLinkType, dataValueParts: string[]): boolean =>
  (deepLinkType === 'ntfn' &&
    (dataValueParts.length < 4 || dataValueParts.length > 5) &&
    dataValueParts.length !== 2) ||
  (deepLinkType === 'custom' && dataValueParts.length !== 2) ||
  (deepLinkType === 'copiedlink' && dataValueParts.length !== 2);

const isTeamsQnAEnabledForMeetingSubType = (deepLinkSubType: string | undefined): boolean =>
  !!deepLinkSubType && (deepLinkSubType.trim() as DeepLinkSubType) === 'teamsQnAEnabledForMeeting';
